import React, { useRef } from 'react'
import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import Styles from './Styles.module.css'

dayjs.extend(customParseFormat)

const DISPLAY_FORMAT = 'DD-MM-YYYY'
const PICKER_FORMAT = 'YYYY-MM-DD'

// Convert the dd-mm-yyyy stored value into the yyyy-mm-dd value the native
// date picker expects. Returns '' when empty or not a valid date.
function toPickerValue(value) {
  if (!value) {
    return ''
  }
  const d = dayjs(value, DISPLAY_FORMAT, true)
  return d.isValid() ? d.format(PICKER_FORMAT) : ''
}

// Calendar / date input. Displays and emits dates as "dd-mm-yyyy" strings.
// A native date picker (hidden) handles selection, so we fully control the
// visible format regardless of the browser locale.
// { label, isRequired, placeholder, disabled, value, onChange, error, min, max }
export default function Calendar(props) {
  const { onChange, value, error, min, max } = props
  const pickerRef = useRef(null)

  // Native picker emits yyyy-mm-dd; convert back to dd-mm-yyyy before emitting.
  const handlePickerChange = e => {
    const raw = e.target.value
    if (!raw) {
      onChange('')
      return
    }
    const d = dayjs(raw, PICKER_FORMAT, true)
    onChange(d.isValid() ? d.format(DISPLAY_FORMAT) : '')
  }

  const openPicker = () => {
    if (props.disabled || !pickerRef.current) {
      return
    }
    if (typeof pickerRef.current.showPicker === 'function') {
      pickerRef.current.showPicker()
    } else {
      pickerRef.current.click()
    }
  }

  return (
    <div className={Styles.inputWrapper}>
      {props.label ? (
        <label className={props.isRequired ? Styles.required : ''}>{props.label}</label>
      ) : null}
      <div
        className={Styles.inputContainer}
        onClick={openPicker}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          cursor: props.disabled ? 'not-allowed' : 'pointer',
        }}
      >
        <input
          type="text"
          readOnly
          disabled={props.disabled}
          value={value || ''}
          placeholder={props.placeholder || 'dd-mm-yyyy'}
          style={{ cursor: props.disabled ? 'not-allowed' : 'pointer' }}
        />
        {/* Default calendar icon (same glyph browsers use on date inputs). */}
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
          style={{ flex: '0 0 auto', color: 'var(--gray-500)' }}
        >
          <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
          <line x1="16" y1="2" x2="16" y2="6" />
          <line x1="8" y1="2" x2="8" y2="6" />
          <line x1="3" y1="10" x2="21" y2="10" />
        </svg>
        {/* Hidden native date picker, opened by clicking the field. */}
        <input
          ref={pickerRef}
          type="date"
          tabIndex={-1}
          aria-hidden="true"
          value={toPickerValue(value)}
          onChange={handlePickerChange}
          min={min}
          max={max}
          style={{
            position: 'absolute',
            width: 0,
            height: 0,
            opacity: 0,
            pointerEvents: 'none',
          }}
        />
      </div>
      <div className={Styles.errorWrapper}>
        {error ? <p className={Styles.error}>{error}</p> : null}
      </div>
    </div>
  )
}
